import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Copy, 
  MoreHorizontal, 
  Trash2, 
  Edit, 
  FileText,
  Image,
  Video,
  Music,
  Archive
} from 'lucide-react';
import type { AssetWithPermission } from '@/services/asset-service';
import { cn } from '@/lib/utils';
import { getRoleLabel, getRoleBadgeColor } from '@/lib/asset-permission-utils';

interface AssetCardProps {
  asset: AssetWithPermission;
  onCopyLink?: (asset: AssetWithPermission) => void;
  onEdit?: (asset: AssetWithPermission) => void;
  onDelete?: (asset: AssetWithPermission) => void;
  className?: string;
}

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <Image className="h-4 w-4" />;
  if (type.startsWith('video/')) return <Video className="h-4 w-4" />;
  if (type.startsWith('audio/')) return <Music className="h-4 w-4" />;
  if (type.includes('zip') || type.includes('compressed')) return <Archive className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
};

export function AssetCard({
  asset,
  onCopyLink,
  onEdit,
  onDelete,
  className
}: AssetCardProps) {
  return (
    <Card className={cn("w-full transition-all hover:shadow-md", className)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-muted">
            {getFileIcon(asset.type)}
          </div>
          
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate font-medium">{asset.name}</h3>
              {asset.permission && (
                <Badge variant="outline" className={getRoleBadgeColor(asset.permission.role)}>
                  {getRoleLabel(asset.permission.role)}
                </Badge>
              )}
            </div>
            
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{asset.type.split('/')[1]?.toUpperCase() || asset.type}</span>
              <span>•</span>
              <span>v{asset.version}</span>
              {asset.updatedAt && (
                <>
                  <span>•</span>
                  <span>{new Date(asset.updatedAt).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
          
          <div className="flex flex-col gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onCopyLink?.(asset)}
              title="Copy Asset Link"
            >
              <Copy className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {}}
              title="More options"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Action buttons for mobile */}
        <div className="mt-3 flex gap-2 sm:hidden">
          {asset.permission && asset.permission.canUpdate && onEdit && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onEdit(asset)}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
          {asset.permission && asset.permission.canDelete && onDelete && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onDelete?.(asset)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}